import type { BadgeVariant } from '../../components/ui'
import { formatDimensions } from '../marketplace-catalog/components/mp-card/model'
import type {
  BlockReason,
  CardChangeMarketplace,
  CardChangeScope,
  CardChangeSendOutcome,
  ChangeStatus,
  ChannelCode,
  JournalApplyJobMetadata,
  JournalBatchSummary,
  MirrorFreshness,
  SourceChangeStatus,
  SourceKind,
} from './types'

export const MARKETPLACE_LABELS: Record<CardChangeMarketplace, string> = {
  wb: 'ВБ',
  ozon: 'OZON',
  ym: 'Я.Маркет',
}

/** `offer` — SPEC_YM_DOCUMENTS.md §4.5: группа полей оффера ym (`tnved`/`certificates`), заведена только у ym-наборов. */
export const SCOPE_LABELS: Record<CardChangeScope, string> = {
  media: 'Фото',
  attributes: 'Характеристики',
  seo: 'SEO',
  offer: 'Реквизиты оффера',
}

/** Порядок чипов групп в строке — фиксированный, не зависит от порядка scopes с бэкенда. */
export const SCOPE_ORDER: CardChangeScope[] = ['media', 'attributes', 'seo', 'offer']

export const BLOCK_REASON_LABELS: Record<BlockReason, string> = {
  seo_switch_off: 'отправка выключена рубильником',
  merge_forbidden: 'через канал изменений не отправляется',
  media_journal_only: 'фото пока не отправляются: сравнение с площадкой по адресу недостоверно',
  visual_not_published: 'визуал ещё не опубликован',
  field_not_editable: 'площадка не принимает это поле через журнал правок',
}

/**
 * `merge_forbidden` — общая причина «это поле трогает склейка, а не канал
 * изменений» (`MERGE_FORBIDDEN`), но у площадок это разные поля: у ВБ и ОЗОН —
 * характеристика/значение группы, у Я.Маркета — пара «параметр 200 + артикул
 * в 14871214» (SPEC_2026-09-13_YANDEX_MARKET.md, §4.3). `seo_switch_off`
 * площадку не называет — общий текст `BLOCK_REASON_LABELS` подходит всем.
 */
export function blockReasonLabel(
  reason: BlockReason,
  marketplace: CardChangeMarketplace,
  /** `field_key` позиции: у `field_not_editable` причина зависит от поля и площадки. */
  fieldKey?: string | null,
): string {
  if (reason === 'field_not_editable') return fieldNotEditableLabel(marketplace, fieldKey)
  if (reason !== 'merge_forbidden') return BLOCK_REASON_LABELS[reason]
  if (marketplace === 'ym') {
    return 'склейка Маркета (группа вариантов/артикул) меняется через склейки'
  }
  return BLOCK_REASON_LABELS.merge_forbidden
}

/**
 * Почему поле из окна карточки не уходит через журнал — те же формулировки, что
 * показывает окно (`window-state.locked_reason`), чтобы человек видел одно и то же
 * и в окне, и на экране изменений.
 */
function fieldNotEditableLabel(marketplace: CardChangeMarketplace, fieldKey?: string | null): string {
  if (marketplace === 'ym') {
    return 'Маркет не принимает это поле через журнал: оно меняется в кабинете Маркета'
  }
  if (marketplace === 'ozon' && fieldKey === 'dimensions') {
    return 'ОЗОН не даёт менять габариты точечно — только полной переотправкой карточки'
  }
  if (marketplace === 'wb' && fieldKey === 'barcode') {
    return 'У ВБ штрихкоды лежат в размерах карточки — отдельной правкой они не отправляются'
  }
  return BLOCK_REASON_LABELS.field_not_editable
}

export const STATUS_LABELS: Record<ChangeStatus, { label: string; variant: BadgeVariant }> = {
  detected: { label: 'Обнаружено', variant: 'blue' },
  approved: { label: 'Подтверждено', variant: 'indigo' },
  sending: { label: 'Отправляется', variant: 'yellow' },
  sent: { label: 'Отправлено', variant: 'green' },
  failed: { label: 'Ошибка', variant: 'red' },
  discarded: { label: 'Отклонено', variant: 'gray' },
  stale: { label: 'Устарело', variant: 'gray' },
}

export const MIRROR_FRESHNESS_LABELS: Record<MirrorFreshness, string> = {
  fresh: 'Свежее',
  warning: 'Может быть устаревшим',
  stale: 'Устаревшее',
  unknown: 'Неизвестно',
}

/** Набор статусов, которые вкладка предлагает в фильтре сверх умолчания бэкенда. */
export const STATUS_FILTER_OPTIONS: Array<{ value: ChangeStatus; label: string }> = (
  Object.keys(STATUS_LABELS) as ChangeStatus[]
).map((value) => ({ value, label: STATUS_LABELS[value].label }))

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * «данные площадки от 06.09, 14:20» — менеджер обязан видеть, на чём основано
 * сравнение. `null` (зеркала ещё не было) отдаём отдельной строкой, не датой.
 */
export function formatMirrorAge(baselineSyncedAt: string | null): string {
  if (!baselineSyncedAt) return 'зеркало ещё не синхронизировано'
  const parsed = new Date(baselineSyncedAt)
  if (Number.isNaN(parsed.getTime())) return 'зеркало ещё не синхронизировано'
  const date = `${pad2(parsed.getDate())}.${pad2(parsed.getMonth() + 1)}`
  const time = `${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`
  return `данные площадки от ${date}, ${time}`
}

/** Подтверждение недоступно, если сравнение опирается на зеркало, которому нельзя доверять. */
export function canApproveFreshness(freshness: MirrorFreshness): boolean {
  return freshness === 'fresh' || freshness === 'warning'
}

export function freshnessBlockedReason(freshness: MirrorFreshness): string | null {
  if (freshness === 'stale') {
    return 'Зеркало площадки устарело — сравнение могло разойтись с реальной карточкой. Обновите зеркало и пересчитайте набор.'
  }
  if (freshness === 'unknown') {
    return 'Возраст зеркала площадки неизвестен — подтверждать отправку не на чем. Пересчитайте набор.'
  }
  return null
}

/**
 * Приглушение чипа группы на экране списка.
 *
 * `ChangeSummary` не содержит разбивку `send_allowed` по группам (она есть
 * только в `ChangeItem[]` детальной карточки) — контракт API менять нельзя,
 * он пишется параллельно бэкендом. Достоверно определить состояние можно
 * только в двух крайних случаях: ничего не заблокировано (никогда не тускнеть)
 * и заблокировано абсолютно всё (`blocked_count === items_count` — тогда
 * тускнеют все чипы строки разом). Промежуточный случай («часть строк набора
 * заблокирована, но неизвестно, из какой именно группы») по summary не
 * различим — там чип остаётся обычным, а точная причина по каждой позиции
 * доступна в модалке разбора, где есть `block_reason` каждой строки.
 */
export function isScopeFullyBlocked(itemsCount: number, blockedCount: number): boolean {
  return itemsCount > 0 && blockedCount >= itemsCount
}

/** Сколько полей реально уедет: заблокированные (`send_allowed = 0`) в подсчёт не входят. */
export function countSendableItems(items: Array<{ send_allowed: 0 | 1 }>): number {
  return items.filter((item) => item.send_allowed === 1).length
}

/**
 * Читаемое представление значения diff-строки (не «Фото»): строка, число,
 * список, объект. Раньше жила локально в `ChangeDetailModal.tsx` — вынесена
 * сюда, чтобы её же переиспользовал `JournalEntryModal.tsx` (было/стало у
 * записи журнала не завязано на площадку и рисуется тем же способом).
 */
export function formatPlainValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.length ? value.map((v) => formatPlainValue(v)).join(', ') : '—'
  if (typeof value === 'object') {
    // Габариты (правка карточки из окна, зеркало площадки): «31 × 22 × 3 см, 0,12 кг», а не JSON.
    return formatDimensionsValue(value) ?? JSON.stringify(value)
  }
  return String(value)
}

const DIMENSION_KEYS = ['length', 'width', 'height', 'weight', 'weightBrutto', 'length_unit', 'weight_unit']
const DIMENSION_VALUE_KEYS = ['length', 'width', 'height', 'weight', 'weightBrutto']
const DIMENSION_AXIS_WORDS: Array<[string, string]> = [
  ['length', 'длина'],
  ['width', 'ширина'],
  ['height', 'высота'],
]

/**
 * Габариты строкой, если объект ими является: ключи — только оси и единицы. Зеркало
 * площадки несёт все оси с единицами (ВБ — см и кг, ОЗОН — мм и г), а позиция правки
 * — только изменённые оси (см и кг); неполный набор подписывается по осям
 * («длина 40 см, вес 0,15 кг»), чтобы «40 × — × —» не выглядело как размер.
 */
function formatDimensionsValue(value: object): string | null {
  const record = value as Record<string, unknown>
  const keys = Object.keys(record)
  if (keys.length === 0 || !keys.every((key) => DIMENSION_KEYS.includes(key))) return null
  if (!keys.some((key) => DIMENSION_VALUE_KEYS.includes(key))) return null
  const has = (key: string) => record[key] !== null && record[key] !== undefined && record[key] !== ''
  if (has('length') && has('width') && has('height')) return formatDimensions(record)
  const lengthUnit = typeof record.length_unit === 'string' && record.length_unit ? record.length_unit : 'см'
  const weightUnit = typeof record.weight_unit === 'string' && record.weight_unit ? record.weight_unit : 'кг'
  const parts: string[] = []
  for (const [key, word] of DIMENSION_AXIS_WORDS) {
    if (has(key)) parts.push(`${word} ${formatNumber(record[key])} ${lengthUnit}`)
  }
  if (has('weight') || has('weightBrutto')) {
    parts.push(`вес ${formatNumber(record.weight ?? record.weightBrutto)} ${weightUnit}`)
  }
  return parts.length > 0 ? parts.join(', ') : null
}

function formatNumber(value: unknown): string {
  const number = Number(value)
  return Number.isFinite(number) ? number.toLocaleString('ru-RU', { maximumFractionDigits: 3 }) : String(value)
}

/**
 * ─────────────────────── Журнал отправок (задача B1) ───────────────────────
 */

/** Ярлыки исхода попытки отправки (contract §7, SPEC_B1_send_log.md) — по спеке задачи D. */
export const SEND_OUTCOME_LABELS: Record<CardChangeSendOutcome, { label: string; variant: BadgeVariant }> = {
  sent: { label: 'Отправлено', variant: 'green' },
  failed: { label: 'Ошибка', variant: 'red' },
  dry_run: { label: 'Пробный прогон', variant: 'gray' },
  channel_disabled: { label: 'Канал выключен', variant: 'yellow' },
  stale: { label: 'Устарело', variant: 'gray' },
  skipped: { label: 'Пропущено', variant: 'gray' },
  // Поздний исход уже ушедшей попытки: площадка приняла запрос, потом отклонила.
  rejected: { label: 'Отклонено площадкой', variant: 'red' },
}

export const SEND_OUTCOME_FILTER_OPTIONS: Array<{ value: CardChangeSendOutcome; label: string }> = (
  Object.keys(SEND_OUTCOME_LABELS) as CardChangeSendOutcome[]
).map((value) => ({ value, label: SEND_OUTCOME_LABELS[value].label }))

/** «Откуда» — send_origin строки журнала отправок (по спеке задачи D). */
export const SEND_ORIGIN_LABELS: Record<string, string> = {
  changes_screen: 'Экран изменений',
  journal: 'Экран правок',
  photo_resend: 'Кнопка «Переотправить фото»',
  card_window: 'Правка в окне карточки',
  source_replace: 'Замена макета',
}

export function sendOriginLabel(origin: string | null): string {
  if (!origin) return '—'
  return SEND_ORIGIN_LABELS[origin] || origin
}

export const SEND_ORIGIN_FILTER_OPTIONS: Array<{ value: string; label: string }> = Object.keys(
  SEND_ORIGIN_LABELS,
).map((value) => ({ value, label: SEND_ORIGIN_LABELS[value] }))

/**
 * ─────────────────────── Журнал правок (Этап 3) ───────────────────────
 */

export const SOURCE_KIND_LABELS: Record<SourceKind, string> = {
  category: 'Категория',
  category_mp_characteristic: 'Характеристика категории',
  mapping: 'Схема сопоставления',
  visual: 'Визуалы',
  card_edit: 'Правка карточки в окне',
}

export const SOURCE_CHANGE_STATUS_LABELS: Record<SourceChangeStatus, { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Ожидает', variant: 'blue' },
  expanding: { label: 'Разворачивается', variant: 'yellow' },
  expanded: { label: 'Развёрнуто', variant: 'indigo' },
  // Решение архитектора 2026-09-15: запись без открытых наборов — переход
  // ленивый на бэкенде, экран только отображает статус, действий нет.
  completed: { label: 'Завершено', variant: 'green' },
  dismissed: { label: 'Отклонено', variant: 'gray' },
}

/**
 * По умолчанию экран показывает то же, что и бэкенд без явного `status`
 * (contract-journal-v1.md §3) — «ожидает», «разворачивается», «развёрнуто»;
 * не отклонённые. Раньше здесь не было `expanding`: пока запись разворачивается,
 * она пропадала из списка по умолчанию — человек нажимал «Развернуть» и терял
 * строку. Приведено к контракту.
 */
export const DEFAULT_JOURNAL_UI_STATUSES: SourceChangeStatus[] = ['pending', 'expanding', 'expanded']

export const JOURNAL_STATUS_FILTER_OPTIONS: Array<{ value: SourceChangeStatus; label: string }> = (
  Object.keys(SOURCE_CHANGE_STATUS_LABELS) as SourceChangeStatus[]
).map((value) => ({ value, label: SOURCE_CHANGE_STATUS_LABELS[value].label }))

export const SOURCE_KIND_FILTER_OPTIONS: Array<{ value: SourceKind; label: string }> = (
  Object.keys(SOURCE_KIND_LABELS) as SourceKind[]
).map((value) => ({ value, label: SOURCE_KIND_LABELS[value] }))

/** Порядок площадок в разбивке затронутых карточек — фиксированный, как `SCOPE_ORDER`. */
export const CHANNEL_ORDER: ChannelCode[] = ['wb', 'ozon', 'ym']

/** `marketplace = null` — правка действует на все площадки разом (contract §2.1). */
export function marketplaceOrAllLabel(marketplace: ChannelCode | null): string {
  return marketplace ? MARKETPLACE_LABELS[marketplace] : 'все площадки'
}

/** «ВБ: 78 129, OZON: 1 204» — только площадки с ненулевым числом, в фиксированном порядке. */
export function formatAffectedBreakdown(breakdown: Partial<Record<ChannelCode, number>> | null | undefined): string {
  if (!breakdown) return '—'
  const parts = CHANNEL_ORDER.filter((code) => Number(breakdown[code]) > 0).map(
    (code) => `${MARKETPLACE_LABELS[code]}: ${Number(breakdown[code]).toLocaleString('ru-RU')}`,
  )
  return parts.length ? parts.join(', ') : '—'
}

/** «Состав, БЖУ, ещё 2» — не больше `max` подписей полей, остальное — счётчиком (§9 ТЗ). */
export function fieldLabelsPreview(labels: string[], max = 3): string {
  if (labels.length === 0) return '—'
  if (labels.length <= max) return labels.join(', ')
  return `${labels.slice(0, max).join(', ')}, ещё ${labels.length - max}`
}

/**
 * Строки «было/стало» для модалки записи журнала — три вида правки хранят
 * `value_before`/`value_after` по-разному (contract-journal-v1.md §2):
 *
 * - `category` — объект `{ колонка: значение }` по каждой из `field_keys`;
 * - `category_mp_characteristic` — одно значение целиком (`field_keys` —
 *   ровно один элемент);
 * - `mapping` — строка схемы сопоставления целиком; показываем только
 *   различающиеся ключи (§9 ТЗ: «не два JSON целиком»), а не сам `field_keys`.
 */
export interface JournalFieldRow {
  key: string
  label: string
  before: unknown
  after: unknown
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

/** Ключи, различающиеся между двумя объектами схемы сопоставления — не весь JSON целиком. */
export function diffMappingKeys(
  before: unknown,
  after: unknown,
): Array<{ key: string; before: unknown; after: unknown }> {
  const beforeRecord = asRecord(before)
  const afterRecord = asRecord(after)
  const keys = Array.from(new Set([...Object.keys(beforeRecord), ...Object.keys(afterRecord)])).sort((a, b) =>
    a.localeCompare(b),
  )
  return keys
    .filter((key) => JSON.stringify(beforeRecord[key]) !== JSON.stringify(afterRecord[key]))
    .map((key) => ({ key, before: beforeRecord[key], after: afterRecord[key] }))
}

/** `{ name, value }` характеристики из записи `card_edit` → само значение; остальное как есть. */
function unwrapAttrValue(value: unknown): unknown {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    const keys = Object.keys(record)
    if ('value' in record && keys.every((key) => key === 'name' || key === 'value')) return record.value
  }
  return value
}

export function journalFieldRows(entry: {
  source_kind: SourceKind
  field_keys: string[]
  field_labels: string[]
  value_before: unknown
  value_after: unknown
}): JournalFieldRow[] {
  // `visual` — своё представление (`journalVisualDiffRows`, ряд миниатюр, а не
  // JSON.stringify списка `{path, checksum}`). Модалка не зовёт эту функцию
  // для `visual`, но экспортируется она отдельно — пустой массив здесь честнее
  // молчаливого JSON-дампа, если функцию всё же вызовут по ошибке.
  if (entry.source_kind === 'visual') return []
  if (entry.source_kind === 'mapping') {
    return diffMappingKeys(entry.value_before, entry.value_after).map((row) => ({
      key: row.key,
      label: row.key,
      before: row.before,
      after: row.after,
    }))
  }
  if (entry.source_kind === 'category') {
    const before = asRecord(entry.value_before)
    const after = asRecord(entry.value_after)
    return entry.field_keys.map((key, index) => ({
      key,
      label: entry.field_labels[index] || key,
      before: before[key],
      after: after[key],
    }))
  }
  if (entry.source_kind === 'card_edit') {
    // Правка одной карточки из окна: значения лежат по ключам полей (title, brand,
    // dimensions, attr:<id>…), а не целиком; характеристика хранится как { name, value }.
    const before = asRecord(entry.value_before)
    const after = asRecord(entry.value_after)
    return entry.field_keys.map((key, index) => ({
      key,
      label: entry.field_labels[index] || key,
      before: unwrapAttrValue(before[key]),
      after: unwrapAttrValue(after[key]),
    }))
  }
  // category_mp_characteristic — единственная характеристика, значение целиком.
  return entry.field_keys.map((key, index) => ({
    key,
    label: entry.field_labels[index] || key,
    before: entry.value_before,
    after: entry.value_after,
  }))
}

/**
 * Предупреждение о масштабе разворота (§10.2 ТЗ, contract §3): правка без
 * привязки к площадке или больше 1000 карточек требует явного текста о
 * масштабе в диалоге подтверждения, не только числа.
 */
export function journalScaleWarning(marketplace: ChannelCode | null, affectedEstimate: number | null): string | null {
  const large = typeof affectedEstimate === 'number' && affectedEstimate > 1000
  if (marketplace === null && large) {
    return `Правка затронет все площадки сразу — ${affectedEstimate.toLocaleString('ru-RU')} карточек. Проверьте число перед подтверждением.`
  }
  if (marketplace === null) {
    return 'Правка действует на все площадки сразу, а не на одну. Проверьте число затронутых карточек перед подтверждением.'
  }
  if (large) {
    return `Затронуто больше 1000 карточек (${affectedEstimate!.toLocaleString('ru-RU')}). Проверьте число перед подтверждением.`
  }
  return null
}

/**
 * Человекочитаемые причины пропуска набора при отправке по правке (contract
 * §3.1). `visual_changed_since_expand` — Этап 5 (§6.2 ТЗ журнала,
 * «Предохранитель перед отправкой»): предохранитель `reconcileVisualPhotoItems`
 * (`marketplace-card-changes-apply.service.ts`) снял визуалы кабинета заново
 * перед отправкой, и они разошлись с тем, что было на момент разворота — весь
 * набор уходит в `stale`, а не только позиция «Фото».
 */
export const JOURNAL_APPLY_SKIP_REASON_LABELS: Record<
  'mixed_pending_changes' | 'awaiting_mirror_sync' | 'visual_changed_since_expand',
  string
> = {
  mixed_pending_changes: 'в наборе есть другая неподтверждённая правка',
  awaiting_mirror_sync: 'карточка ждёт синхронизации зеркала после прошлой отправки',
  visual_changed_since_expand: 'визуал изменился после разворота',
}

/** Причина пропуска из `results[].reason` — распознаём по маркеру в тексте, как `CHANNEL_DISABLED_MARK`. */
export function journalApplySkipReasonLabel(reason: string | null | undefined): string | null {
  if (!reason) return null
  if (reason.includes('mixed_pending_changes')) return JOURNAL_APPLY_SKIP_REASON_LABELS.mixed_pending_changes
  if (reason.includes('awaiting_mirror_sync')) return JOURNAL_APPLY_SKIP_REASON_LABELS.awaiting_mirror_sync
  if (reason.includes('visual_changed_since_expand')) return JOURNAL_APPLY_SKIP_REASON_LABELS.visual_changed_since_expand
  return reason
}

/**
 * Строка счётчиков наборов правки — общая для модалки (`JournalEntryModal.tsx`)
 * и списка (`JournalListView.tsx`), одно место форматирования (задача N):
 * «к отправке» честное (см. `SourceChangeSummary.sets_sendable`), смешанные
 * (`sets_mixed`) добавляются только когда их больше нуля — не загромождать
 * строку нулём в подавляющем большинстве записей.
 */
export function formatJournalSetCounts(entry: { sets_total: number; sets_sendable: number; sets_mixed: number; sets_sent: number }): string {
  const base = `создано ${entry.sets_total.toLocaleString('ru-RU')} / к отправке ${entry.sets_sendable.toLocaleString('ru-RU')} / отправлено ${entry.sets_sent.toLocaleString('ru-RU')}`
  return entry.sets_mixed > 0 ? `${base} / смешано ${entry.sets_mixed.toLocaleString('ru-RU')}` : base
}

/**
 * ─────────────────── Визуалы: было/стало (Этап 5, §6.2) ───────────────────
 *
 * `value_before`/`value_after` записи `source_kind='visual'` — упорядоченный
 * список `{ path, checksum }` визуалов кабинета в том порядке, в котором их
 * отдаёт выгрузка (contract §6.2). Сравнение — по позиции (индексу), а не по
 * поиску одинаковых путей: порядок кадров в карточке значим сам по себе.
 */
export interface JournalVisualItem {
  path: string
  checksum: string | null
}

export interface JournalVisualDiffRow {
  index: number
  before: JournalVisualItem | null
  after: JournalVisualItem | null
  /** Позиция изменилась — разошлись путь или checksum (сравнение и по тому, и по другому, §6.2 ТЗ). */
  changed: boolean
}

function toVisualItem(value: unknown): JournalVisualItem | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const path = record.path === undefined || record.path === null ? '' : String(record.path)
  const checksum = record.checksum === undefined || record.checksum === null ? null : String(record.checksum)
  return { path, checksum }
}

function asVisualList(value: unknown): JournalVisualItem[] {
  if (!Array.isArray(value)) return []
  return value.map((item) => toVisualItem(item)).filter((item): item is JournalVisualItem => item !== null)
}

/** Ряды «было/стало» визуалов кабинета — попарно по позиции, изменённые помечены `changed`. */
export function journalVisualDiffRows(entry: { value_before: unknown; value_after: unknown }): JournalVisualDiffRow[] {
  const before = asVisualList(entry.value_before)
  const after = asVisualList(entry.value_after)
  const length = Math.max(before.length, after.length)
  const rows: JournalVisualDiffRow[] = []
  for (let index = 0; index < length; index++) {
    const b = before[index] ?? null
    const a = after[index] ?? null
    const changed = !b || !a || b.path !== a.path || b.checksum !== a.checksum
    rows.push({ index, before: b, after: a, changed })
  }
  return rows
}

/** «a1b2c3d4» — контрольная сумма коротко для строки; полная — в `title` элемента. */
export function shortChecksum(checksum: string | null): string {
  if (!checksum) return '—'
  return checksum.slice(0, 8)
}

/**
 * ─────────────────── Партии массовой перегенерации визуалов (Этап 5, §6.2) ───────────────────
 */

/**
 * Счётное существительное по-русски: «1 артикул», «2 артикула», «5 артикулов».
 * Тот же алгоритм, что и у `plural` в `marketplace-bundles/presentation.ts`
 * (задача Q5, п.2) — общего модуля склонения в `frontend/src/lib`/`utils` нет
 * (проверено: там нет ни одной функции `plural*`, только `lib/api.ts`,
 * `lib/cn.ts` и подобное), а `marketplace-bundles/presentation.ts` — файл
 * другой фичи, нигде не импортируемый извне неё — заводить кросс-фичевую
 * зависимость ради одной короткой функции не стали, локальная копия того же
 * алгоритма здесь дешевле связывания фич.
 */
export function pluralRu(count: number, forms: [string, string, string]): string {
  const hundreds = Math.abs(count) % 100
  const units = hundreds % 10
  if (hundreds >= 11 && hundreds <= 14) return forms[2]
  if (units === 1) return forms[0]
  if (units >= 2 && units <= 4) return forms[1]
  return forms[2]
}

/** «1 артикул» / «2 артикула» / «5 артикулов» — число + склонённое слово одной строкой. */
export function articlesCountLabel(count: number): string {
  return `${count.toLocaleString('ru-RU')} ${pluralRu(count, ['артикул', 'артикула', 'артикулов'])}`
}

/** «1 запись журнала» / «2 записи журнала» / «5 записей журнала». */
export function journalRecordsCountLabel(count: number): string {
  return `${count.toLocaleString('ru-RU')} ${pluralRu(count, ['запись журнала', 'записи журнала', 'записей журнала'])}`
}

/** «1 набор» / «2 набора» / «5 наборов». */
export function setsCountLabel(count: number): string {
  return `${count.toLocaleString('ru-RU')} ${pluralRu(count, ['набор', 'набора', 'наборов'])}`
}

/** Порядок статусов записи журнала в разбивке партии — как у `SOURCE_CHANGE_STATUS_LABELS`. */
const BATCH_STATUS_ORDER: SourceChangeStatus[] = ['pending', 'expanding', 'expanded', 'completed', 'dismissed']

/** «ожидает 120, развёрнуто 200» — только статусы с ненулевым числом, в фиксированном порядке. */
export function formatBatchStatusBreakdown(breakdown: JournalBatchSummary['status_breakdown']): string {
  const parts = BATCH_STATUS_ORDER.filter((status) => Number(breakdown[status]) > 0).map(
    (status) => `${SOURCE_CHANGE_STATUS_LABELS[status].label.toLowerCase()} ${Number(breakdown[status]).toLocaleString('ru-RU')}`,
  )
  return parts.length ? parts.join(', ') : '—'
}

/** «12.09–15.09» / «12.09.2026» — период записей партии одной строкой. */
export function formatBatchPeriod(minChangedAt: string | null, maxChangedAt: string | null): string {
  const pad2 = (value: number) => String(value).padStart(2, '0')
  const fmt = (value: string) => {
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null
    return `${pad2(parsed.getDate())}.${pad2(parsed.getMonth() + 1)}.${parsed.getFullYear()}`
  }
  const min = minChangedAt ? fmt(minChangedAt) : null
  const max = maxChangedAt ? fmt(maxChangedAt) : null
  if (!min && !max) return '—'
  if (min && max && min !== max) return `${min}–${max}`
  return min || max || '—'
}

/** Число записей партии в статусе `pending` — оно же `expected_count` подтверждения разворота (§7.2 контракта). */
export function batchPendingCount(breakdown: JournalBatchSummary['status_breakdown']): number {
  return Number(breakdown.pending || 0)
}

/** Есть развёрнутые записи — можно предлагать «Отправить по партии» (наборы уже могли появиться). */
export function batchHasExpandedRecords(breakdown: JournalBatchSummary['status_breakdown']): boolean {
  return Number(breakdown.expanded || 0) > 0 || Number(breakdown.completed || 0) > 0
}

/**
 * ─────────────────── Честный вердикт отправки по job.metadata ───────────────────
 *
 * Общее для одиночной отправки (`JournalEntryModal.tsx`) и отправки по партии
 * (`JournalBatchModal.tsx`) — обе задачи (`marketplace_source_change_apply` /
 * `_batch_apply`) кладут в метаданные один и тот же `MarketplaceSourceChangeApplySummary`
 * (см. `services/marketplace/src/services/marketplace-source-change-apply.service.ts`).
 * Перенесено сюда из `JournalEntryModal.tsx` (задача Q3), чтобы не заводить
 * вторую копию — правило CLAUDE.md 7.1: копии в этом проекте расходились трижды.
 */
export const CHANNEL_DISABLED_MARK = 'MP_CARD_CHANGES_SEND_ENABLED'

export interface JournalSendNotice {
  tone: 'success' | 'warning' | 'error'
  text: string
  /** Смешанные наборы (contract §3.1, `summary.mixed`) — показываем отдельной строкой, не смешивая с общим текстом. */
  mixedCount?: number
}

function describeJournalSkipReasons(reasons: string[]): string {
  const labels = Array.from(new Set(reasons.map((reason) => journalApplySkipReasonLabel(reason) || reason)))
  return labels.join('; ')
}

/** Честный вердикт отправки из `job.metadata` — не «раз завершилась, значит отправлено». */
export function journalSendNoticeFromMetadata(metadata: unknown): JournalSendNotice {
  const summary = (metadata || {}) as Partial<JournalApplyJobMetadata>
  const results = Array.isArray(summary.results) ? summary.results : []
  const reasons = results.map((row) => row?.reason).filter((reason): reason is string => Boolean(reason))
  const sent = summary.sent ?? 0
  const total = summary.total ?? results.length
  // Бэкенд (`MarketplaceSourceChangeApplySummary.mixed`) считает наборы,
  // пропущенные по `mixed_pending_changes`, отдельным счётчиком — они же есть
  // в `results[]`, но для отдельной строки на экране берём готовое число, а
  // не пересчитываем по причинам.
  const mixed = summary.mixed ?? 0
  const mixedCount = mixed > 0 ? mixed : undefined

  if (sent > 0) {
    const notSent = total - sent
    if (notSent > 0) {
      return {
        tone: 'warning',
        text: `Отправлено ${sent.toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')} наборов. Не отправлено: ${notSent.toLocaleString('ru-RU')} — ${describeJournalSkipReasons(reasons) || 'причина не указана'}.`,
        mixedCount,
      }
    }
    return { tone: 'success', text: `Отправлено ${sent.toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')} наборов на площадку.`, mixedCount }
  }
  if (reasons.some((reason) => reason.includes(CHANNEL_DISABLED_MARK))) {
    return {
      tone: 'warning',
      text: 'Канал отправки выключен рубильником: ничего не отправлено, наборы остались подтверждёнными.',
      mixedCount,
    }
  }
  if ((summary.stale ?? 0) > 0) {
    return {
      tone: 'warning',
      text: 'Состав части наборов изменился перед отправкой — они не отправлены, пересчитайте правку.',
      mixedCount,
    }
  }
  return {
    tone: 'error',
    text: reasons[0] ? `Отправка не выполнена: ${journalApplySkipReasonLabel(reasons[0]) || reasons[0]}` : 'Отправка не выполнена.',
    mixedCount,
  }
}
